import { Directive, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit, Output, Renderer2, ElementRef } from '@angular/core';
import { IconDefinition, icon } from '@fortawesome/fontawesome-svg-core';
import { faCloudArrowUp } from '@fortawesome/pro-duotone-svg-icons';


/**
 * Diretiva reutilizável para arrastar & soltar arquivos.
 * Aplica classes CSS para estados de drag e emite os arquivos selecionados.
 * Uso:
 * <div appFileDrop (fileDrop)="handle($event)" [fileDropDisabled]="false" [fileDropMultiple]="true"></div>
 */
@Directive({
  selector: '[appFileDrop]',
  standalone: true
})
export class FileDropDirective implements OnInit, OnDestroy {
  /** Desabilita o drop */
  @Input() fileDropDisabled: boolean = false;
  /** Aceita múltiplos arquivos */
  @Input() fileDropMultiple: boolean = true;
  /** Classe aplicada quando arquivos estão sobre a área */
  @Input() fileDropHoverClass: string = 'file-drop-hover';
  /** Texto exibido no overlay */
  @Input() fileDropText: string = 'Solte os arquivos aqui para enviar';
  /** Mostrar overlay? */
  @Input() fileDropShowOverlay: boolean = true;
  /** Classe extra para overlay */
  @Input() fileDropOverlayClass: string = '';
  /** Mostrar ícone */
  @Input() fileDropShowIcon: boolean = true;
  /** Ícone FontAwesome (IconDefinition) */
  @Input() fileDropIcon: IconDefinition = faCloudArrowUp;
  /** Classes extras para o wrapper do ícone (use mb-2 para empilhar acima do texto) */
  @Input() fileDropIconClass: string = 'file-drop-icon text-opacity-75 text-secondary mb-3 fa-4x';
  /** Emite FileList (ou subconjunto) quando soltar */
  @Output() fileDrop = new EventEmitter<FileList>();
  /** Emite boolean quando entra/sai */
  @Output() fileDragState = new EventEmitter<boolean>();

  @HostBinding('class.file-drop') baseClass = true;
  @HostBinding('class.file-drop-disabled') get disabledClass() { return this.fileDropDisabled; }
  @HostBinding('class.file-drop-hovering') hovering = false;

  private overlayEl?: HTMLElement;

  constructor(private _renderer: Renderer2, private _host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    if (this.fileDropShowOverlay) this._createOverlay();
  }
  ngOnDestroy(): void {
    if (this.overlayEl) {
      this._renderer.removeChild(this._host.nativeElement, this.overlayEl);
      this.overlayEl = undefined;
    }
  }

  @HostListener('dragenter', ['$event']) onDragEnter(evt: DragEvent) { this._handleDrag(evt, true); }
  @HostListener('dragover', ['$event']) onDragOver(evt: DragEvent) { this._handleDrag(evt, true); }
  @HostListener('dragleave', ['$event']) onDragLeave(evt: DragEvent) { this._handleDrag(evt, false, true); }
  @HostListener('drop', ['$event']) onDrop(evt: DragEvent) {
    if (this.fileDropDisabled) return;
    evt.preventDefault();
    evt.stopPropagation();
    this.hovering = false;
    this._toggleOverlay(false);
    this.fileDragState.emit(false);
    const files = evt.dataTransfer?.files;
    if (files && files.length > 0) {
      if (!this.fileDropMultiple && files.length > 1) {
        // Cria um FileList apenas com o primeiro arquivo
        const dt = new DataTransfer();
        dt.items.add(files[0]);
        this.fileDrop.emit(dt.files);
      } else {
        this.fileDrop.emit(files);
      }
    }
  }

  private _handleDrag(evt: DragEvent, enter: boolean, leaving: boolean = false) {
    if (this.fileDropDisabled) return;
    if (!evt.dataTransfer?.types.includes('Files')) return;
    evt.preventDefault();
    evt.stopPropagation();

    if (enter) {
      this.hovering = true;
      this._toggleOverlay(true);
      this.fileDragState.emit(true);
    } else if (leaving) {
      const rect = (evt.currentTarget as HTMLElement).getBoundingClientRect();
      const { clientX: x, clientY: y } = evt;
      if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
        this.hovering = false;
        this._toggleOverlay(false);
        this.fileDragState.emit(false);
      }
    }
  }

  private _createOverlay(): void {
    this.overlayEl = this._renderer.createElement('div');
    this._renderer.addClass(this.overlayEl, 'file-drop-overlay');
    this._renderer.addClass(this.overlayEl, 'border');
    this._renderer.addClass(this.overlayEl, 'rounded');
    this._renderer.addClass(this.overlayEl, 'd-flex');
    this._renderer.addClass(this.overlayEl, 'justify-content-center');
    this._renderer.addClass(this.overlayEl, 'align-items-center');

    if (this.fileDropOverlayClass) {
      this.fileDropOverlayClass.split(' ').forEach(c => c && this._renderer.addClass(this.overlayEl!, c));
    }

    const inner = this._renderer.createElement('div');
    this._renderer.addClass(inner, 'file-drop-overlay-content');

    const p = this._renderer.createElement('p');
    ['mb-0','text-center','d-flex','flex-column','align-items-center','justify-content-center']
      .forEach(c=>this._renderer.addClass(p,c));

    if (this.fileDropShowIcon && this.fileDropIcon) {
      const faSvg = icon(this.fileDropIcon).node[0] as SVGElement;
      this.fileDropIconClass.split(' ').forEach(c=>c && faSvg.classList.add(c));
      faSvg.setAttribute('aria-hidden','true');
      this._renderer.appendChild(p, faSvg);
    }

    const spanText = this._renderer.createElement('span');
    const text = this._renderer.createText(this.fileDropText);
    this._renderer.appendChild(spanText, text);
    this._renderer.appendChild(p, spanText);
    this._renderer.appendChild(inner, p);
    this._renderer.appendChild(this.overlayEl, inner);
    this._renderer.appendChild(this._host.nativeElement, this.overlayEl);
    this._toggleOverlay(false);
  }

  private _toggleOverlay(show: boolean) {
    if (!this.overlayEl) return;
    if (show) {
      this._renderer.addClass(this.overlayEl, 'show');
    } else {
      this._renderer.removeClass(this.overlayEl, 'show');
    }
  }
}
